import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useNavigate } from 'react-router-dom'
import { searchBlogPosts as searchBlogPostsApi } from '@/features/blog/api'

// Types for search results
export interface SearchResult {
  id: string
  title: string
  description?: string
  type: 'lead' | 'gallery' | 'service' | 'location' | 'blog' | 'page' | 'social_post' | 'review' | 'user'
  url: string
  subtitle?: string
  status?: string
  metadata?: Record<string, unknown>
  created_at?: string
  updated_at?: string
  relevanceScore?: number
  franchise_region?: string
}

export interface SearchSuggestion {
  query: string
  type: 'recent' | 'popular' | 'suggestion'
  count?: number
}

export interface SearchFilters {
  contentTypes: string[]
  dateRange?: { start: Date; end: Date }
  status?: string[]
  franchiseRegion?: string[]
}

interface SearchState {
  query: string
  isSearching: boolean
  results: SearchResult[]
  suggestions: SearchSuggestion[]
  recentSearches: string[]
  filters: SearchFilters
  totalResults: number
  hasMore: boolean
}

// Helper function to calculate relevance score
type RelevanceItem = {
  title?: string
  description?: string
  content?: string
  client_email?: string
  client_phone?: string
  client_name?: string
  created_at?: string
}

const calculateRelevance = (item: RelevanceItem, query: string, type: string): number => {
  const lowerQuery = query.toLowerCase()
  let score = 0

  // Title match gets highest score
  if (item.title?.toLowerCase().includes(lowerQuery)) {
    score += 10
    if (item.title?.toLowerCase().startsWith(lowerQuery)) {
      score += 5 // Extra points for starts with
    }
  }

  // Description/content match
  if (item.description?.toLowerCase().includes(lowerQuery) || 
      item.content?.toLowerCase().includes(lowerQuery)) {
    score += 5
  }

  // Email/phone/name match for leads
  if (type === 'lead') {
    if (item.client_email?.toLowerCase().includes(lowerQuery)) score += 8
    if (item.client_phone?.includes(query)) score += 8
    if (item.client_name?.toLowerCase().includes(lowerQuery)) score += 10
  }

  // Recent items get slight boost
  if (item.created_at) {
    const daysSinceCreated = (Date.now() - new Date(item.created_at).getTime()) / (1000 * 60 * 60 * 24)
    if (daysSinceCreated < 7) score += 2
    if (daysSinceCreated < 1) score += 3
  }

  return score
}

// Search functions for different content types
const searchLeads = async (query: string, limit = 10): Promise<SearchResult[]> => {
  if (!query.trim()) return []

  try {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .or(`client_name.ilike.%${query}%,client_email.ilike.%${query}%,client_phone.ilike.%${query}%,service_requested.ilike.%${query}%,location.ilike.%${query}%`)
      .limit(limit)

    if (error) {
      console.warn('Leads table not available for search:', error.message)
      return []
    }

    return (data || []).map(lead => ({
      id: lead.id,
      title: lead.client_name,
      description: lead.service_requested || 'Lead inquiry',
      subtitle: `${lead.client_email} • ${lead.location || 'Location not specified'}`,
      type: 'lead' as const,
      url: `/customers/leads?leadId=${lead.id}`,
      status: lead.status,
      created_at: lead.created_at,
      metadata: {
        phone: lead.client_phone,
        email: lead.client_email,
        service: lead.service_requested
      },
      relevanceScore: calculateRelevance(lead, query, 'lead')
    }))
  } catch (error) {
    console.warn('Error searching leads:', error)
    return []
  }
}

const searchGalleries = async (query: string, limit = 10): Promise<SearchResult[]> => {
  if (!query.trim()) return []

  try {
    const { data, error } = await supabase
      .from('galleries')
      .select('*')
      .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
      .limit(limit)

    if (error) {
      console.warn('Galleries table not available for search:', error.message)
      return []
    }

    return (data || []).map(gallery => ({
      id: gallery.id.toString(),
      title: gallery.title,
      description: gallery.description || 'Photo gallery',
      subtitle: `${gallery.franchise_region} • ${gallery.review_status}`,
      type: 'gallery' as const,
      url: `/media/galleries/${gallery.id}`,
      status: gallery.review_status,
      franchise_region: gallery.franchise_region,
      created_at: gallery.created_on,
      metadata: {
        jobNumber: gallery.job_number,
        isPublic: gallery.is_public
      },
      relevanceScore: calculateRelevance(gallery, query, 'gallery')
    }))
  } catch (error) {
    console.warn('Error searching galleries:', error)
    return []
  }
}

const searchServices = async (query: string, limit = 10): Promise<SearchResult[]> => {
  if (!query.trim()) return []

  try {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .or(`title.ilike.%${query}%,description.ilike.%${query}%,content.ilike.%${query}%`)
      .limit(limit)

    if (error) {
      console.warn('Services table not available for search:', error.message)
      return []
    }

    return (data || []).map(service => ({
      id: service.id,
      title: service.title,
      description: service.description,
      subtitle: `Service • ${service.status}`,
      type: 'service' as const,
      url: `/content/services?serviceId=${service.id}`,
      status: service.status,
      created_at: service.created_at,
      metadata: {
        slug: service.slug,
        isFeatured: service.is_featured
      },
      relevanceScore: calculateRelevance(service, query, 'service')
    }))
  } catch (error) {
    console.warn('Error searching services:', error)
    return []
  }
}

const searchLocations = async (query: string, limit = 10): Promise<SearchResult[]> => {
  if (!query.trim()) return []

  try {
    const { data, error } = await supabase
      .from('locations')
      .select('*')
      .or(`name.ilike.%${query}%,description.ilike.%${query}%,city.ilike.%${query}%`)
      .limit(limit)

    if (error) {
      console.warn('Locations table not available for search:', error.message)
      return []
    }

    return (data || []).map(location => ({
      id: location.id,
      title: location.name,
      description: location.description,
      subtitle: `Location • ${location.city || location.franchise_region}`,
      type: 'location' as const,
      url: `/content/locations?locationId=${location.id}`,
      status: location.status,
      franchise_region: location.franchise_region,
      created_at: location.created_at,
      metadata: {
        slug: location.slug,
        isPrimary: location.is_primary
      },
      relevanceScore: calculateRelevance(location, query, 'location')
    }))
  } catch (error) {
    console.warn('Error searching locations:', error)
    return []
  }
}

const searchBlogPosts = async (query: string, limit = 10): Promise<SearchResult[]> => {
  if (!query.trim()) return []

  try {
    // Use feature API for blog search
    const posts = await searchBlogPostsApi(query, limit)

    return posts.map(post => ({
      id: post.id,
      title: post.title,
      description: post.excerpt || 'Blog post',
      subtitle: `Blog • ${post.status}`,
      type: 'blog' as const,
      url: `/content/blog?postId=${post.id}`,
      status: post.status,
      created_at: post.created_at,
      metadata: {
        author: post.author,
        category: post.category,
        tags: post.tags
      },
      relevanceScore: calculateRelevance(post, query, 'blog')
    }))
  } catch (error) {
    console.warn('Error searching blog posts:', error)
    return []
  }
}

const searchSocialPosts = async (query: string, limit = 10): Promise<SearchResult[]> => {
  if (!query.trim()) return []

  try {
    const { data, error } = await supabase
      .from('social_posts')
      .select('*')
      .or(`caption.ilike.%${query}%,content.ilike.%${query}%`)
      .limit(limit)

    if (error) {
      console.warn('Social posts table not available for search:', error.message)
      return []
    }

    return (data || []).map(post => ({
      id: post.id,
      title: post.caption || 'Social Media Post',
      description: post.content || 'Social media post',
      subtitle: `Social • ${post.status}`,
      type: 'social_post' as const,
      url: `/marketing/social?postId=${post.id}`,
      status: post.status,
      created_at: post.created_at,
      metadata: {
        platform: post.platform,
        scheduledTime: post.scheduled_time
      },
      relevanceScore: calculateRelevance(post, query, 'social_post')
    }))
  } catch (error) {
    console.warn('Error searching social posts:', error)
    return []
  }
}

// Main search function that combines all results
const performGlobalSearch = async (
  query: string, 
  filters: SearchFilters = { contentTypes: [] },
  limit = 50
): Promise<{ results: SearchResult[]; totalResults: number }> => {
  if (!query.trim()) {
    return { results: [], totalResults: 0 }
  }

  const searchPromises: Promise<SearchResult[]>[] = []
  const activeTypes = filters.contentTypes.length > 0 ? filters.contentTypes : [
    'lead', 'gallery', 'service', 'location', 'blog', 'social_post'
  ]

  // Execute searches based on active content types
  if (activeTypes.includes('lead')) {
    searchPromises.push(searchLeads(query, Math.ceil(limit / activeTypes.length)))
  }
  if (activeTypes.includes('gallery')) {
    searchPromises.push(searchGalleries(query, Math.ceil(limit / activeTypes.length)))
  }
  if (activeTypes.includes('service')) {
    searchPromises.push(searchServices(query, Math.ceil(limit / activeTypes.length)))
  }
  if (activeTypes.includes('location')) {
    searchPromises.push(searchLocations(query, Math.ceil(limit / activeTypes.length)))
  }
  if (activeTypes.includes('blog')) {
    searchPromises.push(searchBlogPosts(query, Math.ceil(limit / activeTypes.length)))
  }
  if (activeTypes.includes('social_post')) {
    searchPromises.push(searchSocialPosts(query, Math.ceil(limit / activeTypes.length)))
  }

  try {
    const searchResults = await Promise.all(searchPromises)
    let allResults = searchResults.flat()

    // Apply additional filters
    if (filters.status?.length) {
      allResults = allResults.filter(result => 
        !result.status || filters.status!.includes(result.status)
      )
    }

    if (filters.franchiseRegion?.length) {
      allResults = allResults.filter(result => 
        !result.franchise_region || filters.franchiseRegion!.includes(result.franchise_region)
      )
    }

    if (filters.dateRange) {
      allResults = allResults.filter(result => {
        if (!result.created_at) return true
        const resultDate = new Date(result.created_at)
        return resultDate >= filters.dateRange!.start && resultDate <= filters.dateRange!.end
      })
    }

    // Sort by relevance score
    allResults.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0))

    // Limit results
    const limitedResults = allResults.slice(0, limit)

    return {
      results: limitedResults,
      totalResults: allResults.length
    }
  } catch (error) {
    console.error('Error performing global search:', error)
    return { results: [], totalResults: 0 }
  }
}

// Hook for managing search state and functionality
export const useGlobalSearch = () => {
  const navigate = useNavigate()
  const [searchState, setSearchState] = useState<SearchState>({
    query: '',
    isSearching: false,
    results: [],
    suggestions: [],
    recentSearches: JSON.parse(localStorage.getItem('recentSearches') || '[]'),
    filters: { contentTypes: [] },
    totalResults: 0,
    hasMore: false
  })

  // Debounced search query
  const debouncedSearch = useQuery({
    queryKey: ['global-search', searchState.query, searchState.filters],
    queryFn: () => performGlobalSearch(searchState.query, searchState.filters),
    enabled: searchState.query.length >= 2,
    staleTime: 30000,
    refetchOnWindowFocus: false
  })

  // Update search results when query completes
  useEffect(() => {
    if (debouncedSearch.data) {
      setSearchState(prev => ({
        ...prev,
        results: debouncedSearch.data.results,
        totalResults: debouncedSearch.data.totalResults,
        isSearching: false
      }))
    }
  }, [debouncedSearch.data])

  // Update searching state
  useEffect(() => {
    setSearchState(prev => ({
      ...prev,
      isSearching: debouncedSearch.isFetching
    }))
  }, [debouncedSearch.isFetching])

  // Search suggestions based on recent searches and popular terms
  const suggestions = useMemo((): SearchSuggestion[] => {
    if (searchState.query.length === 0) {
      return [
        ...searchState.recentSearches.slice(0, 5).map(query => ({
          query,
          type: 'recent' as const
        })),
        { query: 'pending leads', type: 'suggestion' as const },
        { query: 'approved galleries', type: 'suggestion' as const },
        { query: 'published services', type: 'suggestion' as const },
        { query: 'social posts this week', type: 'suggestion' as const }
      ]
    }
    return []
  }, [searchState.query, searchState.recentSearches])

  const setQuery = (query: string) => {
    setSearchState(prev => ({ ...prev, query, isSearching: query.length >= 2 }))
  }

  const setFilters = (filters: Partial<SearchFilters>) => {
    setSearchState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...filters }
    }))
  }

  const addRecentSearch = (query: string) => {
    if (!query.trim() || searchState.recentSearches.includes(query)) return

    const newRecentSearches = [query, ...searchState.recentSearches.slice(0, 9)]
    localStorage.setItem('recentSearches', JSON.stringify(newRecentSearches))
    setSearchState(prev => ({
      ...prev,
      recentSearches: newRecentSearches
    }))
  }

  const clearRecentSearches = () => {
    localStorage.removeItem('recentSearches')
    setSearchState(prev => ({
      ...prev,
      recentSearches: []
    }))
  }

  const navigateToResult = (result: SearchResult) => {
    addRecentSearch(searchState.query)
    navigate(result.url)
    setQuery('') // Clear search after navigation
  }

  const clearSearch = () => {
    setSearchState(prev => ({
      ...prev,
      query: '',
      results: [],
      totalResults: 0,
      isSearching: false
    }))
  }

  return {
    // State
    query: searchState.query,
    results: searchState.results,
    isSearching: searchState.isSearching,
    suggestions,
    filters: searchState.filters,
    totalResults: searchState.totalResults,
    hasMore: searchState.hasMore,
    recentSearches: searchState.recentSearches,
    
    // Actions
    setQuery,
    setFilters,
    navigateToResult,
    addRecentSearch,
    clearRecentSearches,
    clearSearch,
    
    // Query state
    error: debouncedSearch.error,
    refetch: debouncedSearch.refetch
  }
}

export default useGlobalSearch
